// Innsamlingsstyring (collection management): RFI (Request for Information),
// etterretningskrav (PIR/CCIR/FFIR/EEFI) og innsamlingsoppdrag (tasking). Dette
// lukker OODA-loopen på etterretningssiden: krav → RFI → tasking → svar.
//
// Rent + testbart; DoS-caps på alle fritekst/array-felt.
import { z } from 'zod';
import { TrackOrgSchema } from './track.js';
// ── Etterretningskrav (PIR/CCIR/FFIR/EEFI) ──────────────────────────────────
export const RequirementKind = z.enum(['PIR', 'CCIR', 'FFIR', 'EEFI']);
export const REQUIREMENT_LABEL = {
    PIR: 'PIR — prioritert etterretningsbehov',
    CCIR: 'CCIR — sjefens kritiske informasjonsbehov',
    FFIR: 'FFIR — egne styrkers informasjonsbehov',
    EEFI: 'EEFI — kritisk info fienden ikke må få',
};
export const RequirementSchema = z.object({
    id: z.string().max(64),
    kind: RequirementKind.default('PIR'),
    text: z.string().max(2000),
    /** Indikatorer som besvarer kravet (NAI/observerbare tegn). */
    indicators: z.array(z.string().max(500)).max(50).default([]),
    /** Lavere tall = høyere prioritet. */
    priority: z.number().int().min(1).max(99).default(1),
    createdMs: z.number(),
    /** Hvem kravet tilhører — STEMPLES SERVER-SIDE fra sesjonen ved oppretting
     * (§4 anti-spoof), aldri fra klienten. Uten dette har `settKrav`/`slettKrav`
     * ingen måte å avgjøre om en post «tilhører» kallerens org — se SENS-24
     * funn 3: et blind upsert/delete-på-id kunne ellers overskrive/slette et
     * annet distrikts krav. Valgfritt for bakoverkompatibilitet med poster
     * lagret før dette feltet fantes (behandles som org-uavhengige/globale). */
    org: TrackOrgSchema.optional(),
    // ── RFI-datamodell 2.0 (vale/RFI_SENSORPLASSERING_LLM_PLAN_2026-08-19.md §6)
    // FM 34-2 sitt 5-felts krav-mal WHAT/WHERE/WHEN+LTIOV/WHY/WHO. `text` er
    // allerede WHAT. Alle fire er VALGFRIE — bakoverkompatible som `org` over,
    // ikke en ombygging av et felt som allerede virker.
    /** WHERE — kobling til en navngitt teig/AO (fritekst-referanse, ikke en
     * fremmednøkkel: en teig kan slettes uten at et gammelt krav blir ugyldig). */
    naiRef: z.string().max(64).optional(),
    /** WHEN+LTIOV — Latest Time Information is of Value: når svaret blir
     * verdiløst UANSETT, atskilt fra `Rfi.dueMs` (forventet svarvindu). */
    ltiovMs: z.number().optional(),
    /** WHY — hvorfor kravet finnes, til bruk i tasking-begrunnelse. */
    hvorfor: z.string().max(500).optional(),
    /** WHO — hvem/hvilket ledd som opprinnelig meldte behovet. */
    hvemSpurte: z.string().max(128).optional(),
});
// ── RFI (Request for Information) ────────────────────────────────────────────
export const RfiPriority = z.enum(['routine', 'priority', 'immediate', 'flash']);
export const RFI_PRIORITY_RANK = {
    flash: 0, immediate: 1, priority: 2, routine: 3,
};
export const RfiStatus = z.enum(['open', 'tasked', 'answered', 'closed']);
/**
 * ESKALERINGSSTATUS — en ANNEN akse enn `RfiStatus`, med vilje.
 *
 * Plan §6.1 / forskningsgrunnlaget funn 3 (ATP 2-01/FM 3-55): en RFI er en
 * FALLBACK-mekanisme. Når egne ressurser ikke strekker til, sendes spørsmålet
 * videre til neste ledd, som kan godta, videresende igjen, eller returnere det
 * som ugyldig/utenfor mandat. Det er en ANNEN linje enn «er spørsmålet
 * besvart» (`RfiStatus`): en RFI kan være `videresendt` (eskalering) og
 * samtidig `open` (ikke besvart) — begge sanne, begge nyttige, forvekslet er
 * de en TAPT distinksjon doktrinen holder atskilt.
 */
export const RfiEskaleringsstatus = z.enum(['sendt', 'godtatt', 'videresendt', 'returnert']);
export const RfiSchema = z.object({
    id: z.string().max(64),
    requester: z.string().max(128),
    question: z.string().max(2000),
    priority: RfiPriority.default('routine'),
    status: RfiStatus.default('open'),
    /** Koblet etterretningskrav (PIR-id) om relevant. */
    requirementId: z.string().max(64).optional(),
    createdMs: z.number(),
    /** Frist (ms epoch). */
    dueMs: z.number().optional(),
    /** Valgfritt interesseområde (speiler GNÅ gna_rfi: lat/lon + radius_m), så en
     * felt-RFI fra GNÅ-appen kan vises som sirkel på COP-kartet uten tap. */
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    radiusM: z.number().int().min(0).max(500000).optional(),
    /** Interesseområdet som MGRS (kompakt form) — det operatøren faktisk skrev/
     * leste. UX-funn 8: en RFI uten sted kan ikke prioriteres mot området den
     * gjelder. Valgfritt: en RFI er fortsatt et spørsmål, ikke en observasjon. */
    mgrs: z.string().max(32).optional(),
    answer: z.string().max(8000).optional(),
    answeredBy: z.string().max(128).optional(),
    answeredMs: z.number().optional(),
    /** Eskaleringsakse — se `RfiEskaleringsstatus`. Udefinert = ikke eskalert
     * (spørsmålet er stilt og besvares/tas hånd om der det ble stilt). */
    eskalering: RfiEskaleringsstatus.optional(),
    /** Hvem eskaleringen gikk TIL — meningsfullt kun ved `eskalering: 'videresendt'`. */
    videresendtTil: z.string().max(128).optional(),
    /** Hvorfor en `returnert` RFI ble avvist (ugyldig, utenfor mandat, dublett …). */
    returnertGrunn: z.string().max(500).optional(),
    // ── Vaktsjefkø / lastdeling (DISP-01, B-003 «Dispatch = SKADE») ────────────
    // Zellos Dispatch Hub deler én anropskø mellom operatører, med tildeling så
    // ikke to tar samme anrop. `analystQueue` gir køen (poeng + begrunnelse);
    // dette er det ENE feltet som manglet for at to operatører ikke skal jobbe
    // dobbelt på samme RFI. Udefinert = ingen har tatt den (ledig i køen).
    /** Hvem som har TATT denne RFI-en for aktiv oppfølging (operatørens
     * kallesignal/id) — ANNET enn `CollectionTask.taskedBy`: dette er hvem som
     * EIER spørsmålet i vaktsjefkøen nå, ikke hvilken ressurs som samler inn
     * svaret. STEMPLES SERVER-SIDE fra avsender ved klaiming (§4 anti-spoof,
     * se `collab.ts`) — en operatør kan bare klaime SOM SEG SELV. */
    claimedBy: z.string().max(128).optional(),
    /** Tidspunktet klaim-status sist endret (klaimet ELLER frigitt) — brukes til
     * å avgjøre hvilken kopi som er nyest ved sammenslåing (`mergeRfi`), samme
     * rolle som `answeredMs` har for svar. IKKE «hvor lenge klaimet», bare
     * sist-endret. */
    claimedAtMs: z.number().optional(),
});
/** Parser én (upålitelig) RFI trygt — null om ugyldig. Til collab-inntak. */
export function parseRfiOne(raw) {
    const r = RfiSchema.safeParse(raw);
    return r.success ? r.data : null;
}
// ── Innsamlingsoppdrag (tasking) ────────────────────────────────────────────
export const TaskStatus = z.enum(['assigned', 'collecting', 'reported', 'cancelled']);
export const CollectionTaskSchema = z.object({
    id: z.string().max(64),
    rfiId: z.string().max(64),
    /** Innsamlingsressurs (sensor/enhet/kallesignal) — FRITEKST. Beholdes for
     * ressurser som ikke (ennå) er i kapabilitetsregisteret; se `kapabilitetId`
     * for den strukturerte referansen. */
    asset: z.string().max(128),
    /** SIR→SOR (plan §6.3): referanse til en registrert `Kapabilitet` (se
     * `@skade/contracts` kapabilitet.ts). Når satt, kan tasking-UI vise
     * kapabilitetens EGNE egenskaper (rekkevidde, dag/natt, klar-til-bruk) ved
     * siden av kravets WHEN/LTIOV — operatøren ser om den faktisk kan svare i
     * tide, uten å regne det ut i hodet. Utvider `asset`, erstatter den ikke:
     * en kapabilitet har fortsatt et lesbart navn i `asset`. */
    kapabilitetId: z.string().max(64).optional(),
    discipline: z.string().max(32).default('ALLKILDE'),
    status: TaskStatus.default('assigned'),
    taskedBy: z.string().max(128),
    createdMs: z.number(),
    /** Samme org-stempling som `RequirementSchema.org` — se der for hvorfor. */
    org: TrackOrgSchema.optional(),
});
// ── Hjelpere (utvalg / sortering / statusflyt) ──────────────────────────────
/** RFI-er sortert på hastegrad (flash først), deretter eldste først. */
export function sortRfis(rfis) {
    return [...rfis].sort((a, b) => RFI_PRIORITY_RANK[a.priority] - RFI_PRIORITY_RANK[b.priority] || a.createdMs - b.createdMs);
}
/** Åpne (ubesvarte) RFI-er — open eller tasked. */
export function openRfis(rfis) {
    return sortRfis(rfis.filter((r) => r.status === 'open' || r.status === 'tasked'));
}
/** RFI-er som har passert frist uten svar. */
export function overdueRfis(rfis, now) {
    return openRfis(rfis).filter((r) => r.dueMs !== undefined && r.dueMs < now);
}
/** RFI-er knyttet til et gitt etterretningskrav. */
export function rfisForRequirement(rfis, requirementId) {
    return sortRfis(rfis.filter((r) => r.requirementId === requirementId));
}
/** Registrer et svar på en RFI (setter status = answered). Ren (ny kopi). */
export function answerRfi(rfi, answer, by, atMs) {
    return { ...rfi, status: 'answered', answer: answer.slice(0, 8000), answeredBy: by, answeredMs: atMs };
}
/**
 * Klaim en RFI i vaktsjefkøen (DISP-01) — «jeg jobber denne nå», så en annen
 * operatør ser det og ikke starter samme spørsmål dobbelt. Ren (ny kopi).
 * `by` er klientens PÅSTAND om hvem — se `collab.ts` for hvorfor det uansett
 * stemples til autentisert avsender før fanout (§4 anti-spoof).
 */
export function claimRfi(rfi, by, atMs) {
    return { ...rfi, claimedBy: by, claimedAtMs: atMs };
}
/** Frigi en klaimet RFI — tilbake til ledig i køen. Ren (ny kopi). */
export function releaseRfi(rfi, atMs) {
    const { claimedBy: _drop, ...rest } = rfi;
    return { ...rest, claimedAtMs: atMs };
}
/** Neste naturlige RFI-status (open→tasked→answered→closed); null på slutt. */
export function nextRfiStatus(s) {
    const order = ['open', 'tasked', 'answered', 'closed'];
    const i = order.indexOf(s);
    return i >= 0 && i < order.length - 1 ? order[i + 1] : null;
}
const RFI_STATUS_RANK = { open: 0, tasked: 1, answered: 2, closed: 3 };
// `claimedAtMs` teller også som «berørt» (DISP-01): en klaim/frigjøring er en
// like reell endring som et svar, og må kunne gjøre EN kopi til vinneren selv
// om ingen andre felt endret seg — ellers ville en frigjøring tape mot en
// eldre, fortsatt-klaimet kopi og se ut som den aldri skjedde.
const rfiTouch = (r) => Math.max(r.createdMs, r.answeredMs ?? 0, r.claimedAtMs ?? 0);
/** Monotonisk fletting av to kopier av samme RFI (sanntid): feltinnhold fra den
 * sist berørte, men status kan ALDRI gå bakover (hindrer at en gammel/forfalsket
 * kopi «reverter» en besvart/lukket RFI til åpen eller sletter svaret). Klaim-
 * status (DISP-01) følger samme «nyeste vinner»-regel som resten av feltene,
 * via `winner` — en frigjøring (nyere `claimedAtMs`, `claimedBy` fraværende)
 * slår altså en eldre, fortsatt-klaimet kopi. */
export function mergeRfi(a, b) {
    const winner = rfiTouch(b) >= rfiTouch(a) ? b : a;
    const status = RFI_STATUS_RANK[a.status] >= RFI_STATUS_RANK[b.status] ? a.status : b.status;
    // Bevar svaret hvis én av dem har det og vinneren mangler det (ikke mist data).
    const answered = a.answer && !winner.answer ? a : (b.answer && !winner.answer ? b : winner);
    return { ...winner, status, answer: answered.answer, answeredBy: answered.answeredBy, answeredMs: answered.answeredMs };
}
//# sourceMappingURL=rfi.js.map